import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@repo/ui/box'
import InnerBox from '@repo/ui/inner-box'
import { useQuery } from '@tanstack/react-query'
import { CountryCode } from 'libphonenumber-js'
import { useForm } from 'react-hook-form'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../../api/countries/get-countries-list'
import { useNativeCountryField } from '../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CoachCompanyInput } from '../../../../lib/types/models/ol/coach-company/coach-company-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getCountryFlag } from '../../../../lib/utils/functions/country.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import { ControlledAdressInput } from '../../../elements/inputs/address-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { COACH_COMPANY_SCHEMA_VALIDATION } from '../utils'

type Props = BaseFormProps<CoachCompanyInput> & {
  className?: string
  country: string
}

const CoachCompanyForm = ({ defaultValues, country, ...validationButtonProps }: Props) => {
  const { t } = useTranslate()
  const form = useForm<CoachCompanyInput>({
    resolver: zodResolver(COACH_COMPANY_SCHEMA_VALIDATION(t)),
    defaultValues,
    mode: FORM_MODE
  })
  useNativeCountryField(form, 'country')

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  if (!countryList) return null

  const code = getCountryFlag(country, countryList.data)?.alpha2 as CountryCode

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <Box>
          <InnerBox title="Coach company" className="rounded-xl flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        required
                        {...field}
                        label={t('coach.company.form.name')}
                        type={'text'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledPhoneInput
                        {...field}
                        label={t('coach.company.form.phone.number')}
                        defaultCountry={code}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergencyNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledPhoneInput
                        {...field}
                        label={t('coach.company.form.emergency.number')}
                        value={field.value ?? ''}
                        defaultCountry={code}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-3">
              <FormField
                control={form.control}
                name="address1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledAdressInput
                        nativeCountryNames
                        {...field}
                        required
                        label={t('coach.company.form.address1')}
                        onDetailsChange={(placeDetails) => {
                          form.setValue('address1', placeDetails.address, { shouldValidate: true })
                          form.setValue('zipCode', placeDetails.postalCode, {
                            shouldValidate: true
                          })
                          form.setValue('city', placeDetails.city, { shouldValidate: true })
                          form.setValue('country', placeDetails.country, { shouldValidate: true })
                          form.trigger(['address1', 'zipCode', 'city', 'country'])
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address2"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        label={t('coach.company.form.address2')}
                        type={'text'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          required
                          {...field}
                          label={t('coach.company.form.city')}
                          type={'text'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledInput
                          required
                          {...field}
                          label={t('coach.company.form.zip.code')}
                          type={'text'}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormControl>
                      <ControlledInput
                        required
                        {...field}
                        label={t('coach.company.form.country')}
                        type={'text'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="comments"
              render={({ field }) => (
                <FormControl>
                  <ControlledInput
                    {...field}
                    label={t('coach.company.form.comments')}
                    type="textarea"
                  />
                </FormControl>
              )}
            />
            <ValidationButtons {...validationButtonProps} />
          </InnerBox>
        </Box>
      </form>
    </Form>
  )
}

export default CoachCompanyForm
